/**
 * Project Managers Directory/Marketplace Page
 * Browse and search for project managers with filtering
 */

import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Card, CardContent, Box, Button, Avatar,
    Chip, TextField, InputAdornment, FormControl, InputLabel, Select,
    MenuItem, Paper, Stack, Rating, Divider, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    Alert, Pagination, CardActions, Badge, Tabs, Tab, List, ListItem,
    ListItemText, ListItemIcon, AlertTitle
} from '@mui/material';
import {
    Search, FilterList, LocationOn, Star, Verified, SupervisorAccount,
    CheckCircle, Phone, Email, Info, Business, TrendingUp, ArrowBack, Message,
    CalendarToday
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import projectManagerService from '../services/projectManagerService';
import EnhancedProjectManagerDiscovery from '../components/projectManagers/EnhancedProjectManagerDiscovery';
import DialogCloseButton from '../components/common/DialogCloseButton';

const ProjectManagersPage = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // Get ID from URL if viewing individual profile
    const { user } = useAuth();
    
    // State
    const [projectManagers, setProjectManagers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedPM, setSelectedPM] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState(0); // 0 = AI Matching, 1 = Standard
    
    // Filters
    const [filters, setFilters] = useState({
        city: '',
        minRating: 0,
        specialization: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('average_rating');
    
    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(12);
    
    // State for individual profile view
    const [profileView, setProfileView] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    
    // Load project managers or individual profile
    useEffect(() => {
        if (id) {
            // If ID is in URL, load individual profile
            loadProjectManagerProfile(id);
        } else {
            // Otherwise, load directory listing
            loadProjectManagers();
        }
    }, [id, page, searchTerm, filters, sortBy]);
    
    const loadProjectManagers = async () => {
        try {
            setLoading(true);
            setError('');
            
            const apiFilters = {
                searchTerm: searchTerm || undefined,
                city: filters.city || undefined,
                minRating: filters.minRating > 0 ? filters.minRating : undefined,
                specialization: filters.specialization || undefined,
                isActive: true
            };
            
            const pagination = {
                page,
                limit,
                sortBy,
                sortOrder: 'desc'
            };
            
            const response = await projectManagerService.getAllProjectManagers(apiFilters, pagination);
            
            if (response && response.data && Array.isArray(response.data)) {
                // Transform project managers to match EnhancedProjectManagerDiscovery format
                // Keep original properties for Standard View compatibility
                const transformedPMs = response.data.map(pm => ({
                    // Original API properties (for Standard View)
                    ...pm,
                    id: pm.id,
                    firstName: pm.firstName || pm.first_name,
                    lastName: pm.lastName || pm.last_name,
                    fullName: pm.fullName || `${pm.firstName || pm.first_name} ${pm.lastName || pm.last_name}`,
                    averageRating: pm.averageRating || 0,
                    totalReviews: pm.totalReviews || pm.total_reviews || 0,
                    profileImageUrl: pm.profileImageUrl || pm.profile_image_url,
                    phoneNumber: pm.phoneNumber || pm.phone_number,
                    city: pm.city,
                    specializations: pm.specializations || [],
                    yearsExperience: pm.yearsExperience || pm.years_experience || 0,
                    completedProjects: pm.completedProjects || pm.completed_projects || 0,
                    emailVerified: pm.emailVerified || pm.email_verified || false,
                    bio: pm.bio,
                    // Transformed properties (for EnhancedProjectManagerDiscovery)
                    name: pm.fullName || `${pm.firstName || pm.first_name} ${pm.lastName || pm.last_name}`,
                    type: 'Project Manager',
                    avatar: pm.profileImageUrl || pm.profile_image_url,
                    rating: pm.averageRating || 0,
                    reviewCount: pm.totalReviews || pm.total_reviews || 0,
                    verified: pm.emailVerified || pm.email_verified || false,
                    verifiedBy: (pm.emailVerified || pm.email_verified) ? ['Eqaya Platform'] : [],
                    location: pm.city || 'Not specified',
                    specialties: pm.specializations || [],
                    experience: (pm.yearsExperience || pm.years_experience) ? `${pm.yearsExperience || pm.years_experience} years` : 'Not specified',
                    onTimeRate: pm.onTimeRate || 0,
                    responseRate: pm.responseRate || 0,
                    trustScore: pm.trustScore || 0,
                    phone: pm.phoneNumber || pm.phone_number,
                    description: pm.bio || 'Professional project manager',
                    availability: 'Available',
                    languages: ['English'],
                    team: 'Individual',
                    disputes: 0,
                    resolvedDisputes: 0,
                    redFlags: [],
                    recentReviews: [],
                    portfolio: [],
                    priceTransparency: {}
                }));
                setProjectManagers(transformedPMs);
                setTotalPages(response.pagination?.totalPages || 1);
            } else {
                setProjectManagers([]);
                setTotalPages(1);
            }
            
        } catch (err) {
            console.error('Failed to load project managers:', err);
            setError(err.message || 'Failed to load project managers');
            setProjectManagers([]);
        } finally {
            setLoading(false);
        }
    };
    
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };
    
    const loadProjectManagerProfile = async (pmId) => {
        try {
            setProfileLoading(true);
            setError('');
            const profile = await projectManagerService.getProjectManagerById(pmId);
            setProfileView(profile);
        } catch (err) {
            console.error('Failed to load project manager profile:', err);
            setError(err.message || 'Failed to load project manager profile');
            setProfileView(null);
        } finally {
            setProfileLoading(false);
        }
    };
    
    const handleViewProfile = (pm) => {
        navigate(`/project-managers/${pm.id}`);
    };
    
    const handleBackToList = () => {
        navigate('/project-managers');
    };
    
    const handleContactEmail = (pm) => {
        const subject = encodeURIComponent(`Inquiry about Project Management Services - ${pm.firstName} ${pm.lastName}`);
        const body = encodeURIComponent(`Hello ${pm.firstName},\n\nI found your profile on Eqaya and I'm interested in your project management services.\n\nBest regards`);
        window.open(`mailto:${pm.email}?subject=${subject}&body=${body}`, '_blank');
    };
    
    const handleContactWhatsApp = (pm) => {
        const phoneNumber = pm.phoneNumber?.replace(/[^0-9]/g, '') || '';
        const message = encodeURIComponent(`Hello ${pm.firstName}, I found your profile on Eqaya and I'm interested in your project management services.`);
        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    };
    
    const handleViewDetails = async (pm) => {
        try {
            const fullProfile = await projectManagerService.getProjectManagerById(pm.id);
            setSelectedPM(fullProfile);
            setDetailsDialog(true);
        } catch (err) {
            setError(err.message || 'Failed to load project manager details');
        }
    };
    
    const clearFilters = () => {
        setSearchTerm('');
        setFilters({ city: '', minRating: 0, specialization: '' });
        setPage(1);
    };
    
    // PM Specializations (matching onboarding options)
    const pmSpecializations = [
        'Residential Construction',
        'Commercial Construction',
        'Industrial Projects',
        'Renovation & Remodeling',
        'Infrastructure',
        'Multi-family Housing',
        'Custom Homes',
        'Green/Sustainable Building'
    ];
    
    // If viewing individual profile
    if (id && profileView) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ mb: 3 }}
                >
                    Back to Project Managers
                </Button>
                
                <Paper sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 3 }}>
                        <Avatar
                            src={profileView.profileImageUrl}
                            sx={{ width: 120, height: 120 }}
                        >
                            {profileView.firstName?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h4" gutterBottom>
                                {profileView.firstName} {profileView.lastName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Star sx={{ color: '#FFD700', fontSize: 24 }} />
                                <Typography variant="h6">
                                    {(profileView.averageRating || 0).toFixed(1)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ({profileView.totalReviews} reviews)
                                </Typography>
                            </Box>
                            {profileView.city && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {profileView.city}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    
                    <Divider sx={{ my: 3 }} />
                    
                    {profileView.bio && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>About</Typography>
                            <Typography variant="body1" color="text.secondary">
                                {profileView.bio}
                            </Typography>
                        </Box>
                    )}
                    
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="primary">
                                    {profileView.totalProjects}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Projects
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="success.main">
                                    {profileView.completedProjects}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completed
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="warning.main">
                                    {profileView.yearsExperience}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Years Experience
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="info.main">
                                    {(profileView.averageRating || 0).toFixed(1)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Average Rating
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                    
                    {profileView.specializations && profileView.specializations.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Specializations</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {profileView.specializations.map((spec, idx) => (
                                    <Chip key={idx} label={spec.replace('_', ' ')} />
                                ))}
                            </Stack>
                        </Box>
                    )}
                    
                    {profileView.reviews && profileView.reviews.length > 0 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>Recent Reviews</Typography>
                            <Stack spacing={2}>
                                {profileView.reviews.map((review) => (
                                    <Paper key={review.id} sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2">
                                                {review.reviewerName}
                                            </Typography>
                                            <Rating value={review.rating} readOnly size="small" />
                                        </Box>
                                        {review.title && (
                                            <Typography variant="subtitle1" gutterBottom>
                                                {review.title}
                                            </Typography>
                                        )}
                                        {review.reviewText && (
                                            <Typography variant="body2" color="text.secondary">
                                                {review.reviewText}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(review.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    
                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<Email />}
                            onClick={() => handleContactEmail(profileView)}
                        >
                            Contact via Email
                        </Button>
                        {profileView.phoneNumber && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<Phone />}
                                    onClick={() => handleContactWhatsApp(profileView)}
                                >
                                    WhatsApp
                                </Button>
                                <Button
                                    variant="outlined"
                                    component="a"
                                    href={`tel:${profileView.phoneNumber}`}
                                    startIcon={<Phone />}
                                >
                                    Call
                                </Button>
                            </>
                        )}
                    </Box>
                </Paper>
            </Container>
        );
    }
    
    // Loading state for profile view
    if (id && profileLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }
    
    // Directory listing view
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                Find Project Managers
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Eqaya Accountability System:</strong> All project managers are rated based on completed projects, 
                    on-time delivery, and dispute resolution. Look for verified badges and check recent reviews from previous clients.
                </Typography>
            </Alert>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={viewMode} onChange={(e, val) => setViewMode(val)}>
                    <Tab label="AI Matching" />
                    <Tab label="Standard View" />
                </Tabs>
            </Paper>

            {/* AI Matching View */}
            {viewMode === 0 && (
                <EnhancedProjectManagerDiscovery 
                    projectManagers={projectManagers}
                    projectType=""
                    location=""
                    budget={0}
                />
            )}

            {/* Standard View */}
            {viewMode === 1 && (
                <>
                    {/* Hero Header */}
                    <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 4,
                    background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
                    color: 'white',
                    borderRadius: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Find Project Managers
                        </Typography>
                        <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                            Browse our network of experienced project managers for your construction projects
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ textAlign: 'center', bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {projectManagers.length}
                            </Typography>
                            <Typography variant="caption">Available</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                4.5+
                            </Typography>
                            <Typography variant="caption">Avg Rating</Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
            
            {/* Search Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder="Search by name or location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortBy}
                                label="Sort By"
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <MenuItem value="average_rating">Highest Rated</MenuItem>
                                <MenuItem value="total_projects">Most Projects</MenuItem>
                                <MenuItem value="completed_projects">Most Completed</MenuItem>
                                <MenuItem value="years_experience">Most Experienced</MenuItem>
                                <MenuItem value="created_at">Newest</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant={showFilters ? 'contained' : 'outlined'}
                            startIcon={<FilterList />}
                            onClick={() => setShowFilters(!showFilters)}
                            sx={{ height: '56px' }}
                        >
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Advanced Filters Row */}
            {showFilters && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="City"
                                value={filters.city}
                                onChange={(e) => handleFilterChange({ ...filters, city: e.target.value })}
                                placeholder="Filter by city"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Specialization</InputLabel>
                                <Select
                                    value={filters.specialization}
                                    label="Specialization"
                                    onChange={(e) => handleFilterChange({ ...filters, specialization: e.target.value })}
                                >
                                    <MenuItem value="">All Specializations</MenuItem>
                                    {pmSpecializations.map((spec) => (
                                        <MenuItem key={spec} value={spec}>
                                            {spec}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Minimum Rating</InputLabel>
                                <Select
                                    value={filters.minRating}
                                    label="Minimum Rating"
                                    onChange={(e) => handleFilterChange({ ...filters, minRating: e.target.value })}
                                >
                                    <MenuItem value={0}>Any Rating</MenuItem>
                                    <MenuItem value={3}>3+ Stars</MenuItem>
                                    <MenuItem value={4}>4+ Stars</MenuItem>
                                    <MenuItem value={4.5}>4.5+ Stars</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={clearFilters}
                                sx={{ height: '56px' }}
                            >
                                Clear Filters
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            
            {/* Results Count */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : `${projectManagers.length} project managers found`}
                </Typography>
            </Box>
            
            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}
            
            {/* Project Managers Grid */}
            {!loading && projectManagers.length > 0 && (
                <>
                    <Grid container spacing={3}>
                        {projectManagers.map((pm) => {
                            const experienceYears = pm.yearsExperience || pm.experience?.match(/\d+/)?.[0] || 0;
                            const pmType = pm.type || 'Project Manager';
                            const rating = pm.averageRating || pm.rating || 0;
                            const reviewCount = pm.totalReviews || pm.reviewCount || 0;
                            const completedProjects = pm.completedProjects || 0;
                            const onTimeRate = pm.onTimeRate || 0;
                            const responseRate = pm.responseRate || 0;
                            const trustScore = pm.trustScore || 0;
                            
                            return (
                                <Grid item xs={12} md={6} key={pm.id}>
                                    <Card 
                                        sx={{ 
                                            height: '100%',
                                            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 4
                                            }
                                        }}
                                    >
                                        <CardContent>
                                            {/* Header: Avatar, Name, Type, Experience, Rating */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                                                        <SupervisorAccount />
                                                    </Avatar>
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="h6">{pm.name || `${pm.firstName} ${pm.lastName}`}</Typography>
                                                            {pm.verified && (
                                                                <Verified color="primary" fontSize="small" />
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {pmType} • {experienceYears} years
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Rating value={rating} precision={0.1} readOnly size="small" />
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {rating.toFixed(1)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({reviewCount} reviews)
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Description */}
                                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                                {pm.description || pm.bio || 'Professional project manager with proven track record.'}
                                            </Typography>

                                            {/* Specializations/Tags */}
                                            {(pm.specialties || pm.specializations) && (pm.specialties || pm.specializations).length > 0 && (
                                                <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                                                    {(pm.specialties || pm.specializations).slice(0, 3).map((specialty, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={specialty}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            )}

                                            <Divider sx={{ my: 2 }} />

                                            {/* Performance Metrics */}
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h6" color="primary">
                                                            {completedProjects}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Projects
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Chip 
                                                            label={`${onTimeRate}%`} 
                                                            size="small" 
                                                            color={onTimeRate >= 90 ? 'success' : onTimeRate >= 75 ? 'warning' : 'error'}
                                                        />
                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                            On Time
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h6" color="primary">
                                                            {responseRate}%
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Response
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h6" color="primary">
                                                            {trustScore}%
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Trust Score
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            <Divider sx={{ my: 2 }} />

                                            {/* Location, Availability, Verification, Actions */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip 
                                                        icon={<LocationOn />} 
                                                        label={pm.location || pm.city || 'Not specified'} 
                                                        size="small" 
                                                    />
                                                    <Chip 
                                                        icon={<CheckCircle />} 
                                                        label={pm.availability || 'Available'} 
                                                        size="small" 
                                                        color={(pm.availability || 'Available') === 'Available' ? 'success' : 'warning'}
                                                    />
                                                    {pm.verified && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <CheckCircle fontSize="small" color="primary" />
                                                            <Typography variant="caption" color="text.secondary">
                                                                Eqaya Platform
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={() => navigate(`/messages?projectManagerId=${pm.id}`)}
                                                        title="Send Message"
                                                    >
                                                        <Message />
                                                    </IconButton>
                                                    <Button 
                                                        variant="contained" 
                                                        size="small"
                                                        onClick={() => handleViewDetails(pm)}
                                                    >
                                                        View Details
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
            
            {/* No Results */}
            {!loading && projectManagers.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        No project managers found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Try adjusting your filters or search term
                    </Typography>
                    <Button variant="outlined" onClick={clearFilters}>
                        Clear All Filters
                    </Button>
                </Paper>
            )}
            
            {/* Details Dialog */}
            <Dialog
                open={detailsDialog}
                onClose={() => {
                    setTimeout(() => setDetailsDialog(false), 10);
                }}
                maxWidth="md"
                fullWidth
                TransitionProps={{
                    onExited: () => {
                        setSelectedPM(null);
                        setActiveTab(0);
                    }
                }}
            >
                <DialogCloseButton onClick={() => { setTimeout(() => setDetailsDialog(false), 10); }} />
                {selectedPM && (
                    <>
                        <DialogTitle>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                                        <SupervisorAccount />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6">
                                            {selectedPM.name || `${selectedPM.firstName} ${selectedPM.lastName}`}
                                            {selectedPM.verified && (
                                                <Verified color="primary" fontSize="small" sx={{ ml: 1 }} />
                                            )}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedPM.type || 'Project Manager'} • {selectedPM.location || selectedPM.city || 'Not specified'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip 
                                    label={`Trust Score: ${selectedPM.trustScore || 0}%`} 
                                    color="primary"
                                />
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 3 }}>
                                <Tab label="Overview" />
                                <Tab label="Reviews" />
                                <Tab label="Pricing" />
                                <Tab label="Work History" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2 }}>
                                                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                                                <List dense>
                                                    <ListItem>
                                                        <ListItemIcon><CalendarToday /></ListItemIcon>
                                                        <ListItemText primary={selectedPM.availability || 'Available'} />
                                                    </ListItem>
                                                    <ListItem>
                                                        <ListItemIcon><Message /></ListItemIcon>
                                                        <ListItemText 
                                                            primary="Send Message" 
                                                            secondary="Communicate via messaging platform"
                                                        />
                                                    </ListItem>
                                                </List>
                                                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Message />}
                                                        onClick={() => {
                                                            navigate(`/messages?projectManagerId=${selectedPM.id}`);
                                                            setDetailsDialog(false);
                                                        }}
                                                        fullWidth
                                                    >
                                                        Open Messaging
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2 }}>
                                                <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Completion Rate
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', height: 8, borderRadius: 1 }}>
                                                                <Box 
                                                                    sx={{ 
                                                                        width: `${selectedPM.onTimeRate || 0}%`, 
                                                                        bgcolor: (selectedPM.onTimeRate || 0) >= 90 ? 'success.main' : (selectedPM.onTimeRate || 0) >= 75 ? 'warning.main' : 'error.main',
                                                                        height: '100%',
                                                                        borderRadius: 1
                                                                    }} 
                                                                />
                                                            </Box>
                                                            <Typography variant="body2">{selectedPM.onTimeRate || 0}%</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Response Rate
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', height: 8, borderRadius: 1 }}>
                                                                <Box 
                                                                    sx={{ 
                                                                        width: `${selectedPM.responseRate || 0}%`, 
                                                                        bgcolor: 'primary.main',
                                                                        height: '100%',
                                                                        borderRadius: 1
                                                                    }} 
                                                                />
                                                            </Box>
                                                            <Typography variant="body2">{selectedPM.responseRate || 0}%</Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Paper sx={{ p: 2 }}>
                                                <Typography variant="h6" gutterBottom>About</Typography>
                                                <Typography variant="body2" paragraph>
                                                    {selectedPM.description || selectedPM.bio || 'Professional project manager with proven track record.'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Languages:</strong> {(selectedPM.languages || ['English']).join(', ')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Team Size:</strong> {selectedPM.team || 'Individual'}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box>
                                    {selectedPM.recentReviews && selectedPM.recentReviews.length > 0 ? (
                                        selectedPM.recentReviews.map((review) => (
                                            <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Box>
                                                        <Typography variant="subtitle2">
                                                            {review.reviewerName || review.reviewer || 'Anonymous'}
                                                            {review.verified && (
                                                                <Verified color="primary" fontSize="small" sx={{ ml: 1 }} />
                                                            )}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : review.date || 'Recently'}
                                                        </Typography>
                                                    </Box>
                                                    <Rating value={review.rating || 0} readOnly size="small" />
                                                </Box>
                                                <Typography variant="body2">
                                                    {review.reviewText || review.comment || 'No comment provided'}
                                                </Typography>
                                            </Paper>
                                        ))
                                    ) : (
                                        <Alert severity="info">
                                            <AlertTitle>No Reviews Yet</AlertTitle>
                                            This project manager hasn't received any reviews yet.
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            {activeTab === 2 && (
                                <Box>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            These are approximate prices. Final quotes may vary based on specific project requirements.
                                        </Typography>
                                    </Alert>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>Transparent Pricing</Typography>
                                        {selectedPM.priceTransparency && Object.keys(selectedPM.priceTransparency).length > 0 ? (
                                            <List>
                                                {Object.entries(selectedPM.priceTransparency).map(([service, price]) => (
                                                    <ListItem key={service}>
                                                        <ListItemText 
                                                            primary={service.charAt(0).toUpperCase() + service.slice(1).replace(/([A-Z])/g, ' $1')}
                                                            secondary={price}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Alert severity="info">
                                                Request a custom quote for detailed pricing
                                            </Alert>
                                        )}
                                    </Paper>
                                </Box>
                            )}

                            {activeTab === 3 && (
                                <Box>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>Work History</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Projects: {selectedPM.totalProjects || selectedPM.completedProjects || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Completed Projects: {selectedPM.completedProjects || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Years Experience: {selectedPM.yearsExperience || selectedPM.experience?.match(/\d+/)?.[0] || 0}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDetailsDialog(false)}>Close</Button>
                            <Button 
                                variant="contained" 
                                startIcon={<Message />}
                                onClick={() => {
                                    navigate(`/messages?projectManagerId=${selectedPM.id}`);
                                    setDetailsDialog(false);
                                }}
                            >
                                Contact via Eqaya
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
                </>
            )}
        </Container>
    );
};

export default ProjectManagersPage;

